/**
 * Dispatch deletes are sticky — same spirit as Mark Complete in 0.3.69.
 *
 * A local Inbox / ticket / Trip Log delete (desk or Megan after spoken yes)
 * is company truth. Pull merges; it must not revive those ids from an older
 * Nanode snapshot. Tombstones live on the company so the next Push publishes
 * them and a later Pull cannot put the rows back.
 */
import { hydrateCompany, hydrateRemovedCrewIds, unionRemovedCrewIds } from "./company";
import { mergeActiveFlightBinding, stampCompanyActiveFlight } from "./flightBinding";
import {
  ensureTombstoneRecords,
  gcTimedTombstones,
  hydrateTombstoneRecords,
  mergeTimedTombstones,
  tombstoneIds,
  type TimedTombstone,
  type TombstoneKind,
} from "./rowRevision";
import { preserveAuthoritativeLogs, preserveAuthoritativeTickets } from "./ticketComplete";
import type { CharterRequest, CompanyProfile, FlightLogEntry, Mission } from "./types";

export type { TimedTombstone, TombstoneKind };

export type BoardTombstones = {
  removedInboxIds: string[];
  removedMissionIds: string[];
  removedFlightLogIds: string[];
  records: TimedTombstone[];
};

export const EMPTY_BOARD_TOMBSTONES: BoardTombstones = {
  removedInboxIds: [],
  removedMissionIds: [],
  removedFlightLogIds: [],
  records: [],
};

export const hydrateRemovedIds = hydrateRemovedCrewIds;
export const unionRemovedIds = unionRemovedCrewIds;

export function readBoardTombstones(
  company?: Partial<CompanyProfile> | null,
): BoardTombstones {
  return {
    removedInboxIds: hydrateRemovedIds(company?.removedInboxIds),
    removedMissionIds: hydrateRemovedIds(company?.removedMissionIds),
    removedFlightLogIds: hydrateRemovedIds(company?.removedFlightLogIds),
    records: hydrateTombstoneRecords(company?.boardTombstoneRecords),
  };
}

export function mergeBoardTombstones(
  local?: Partial<CompanyProfile> | null,
  remote?: Partial<CompanyProfile> | null,
  atIso = "",
): BoardTombstones {
  const removedInboxIds = unionRemovedIds(local?.removedInboxIds, remote?.removedInboxIds);
  const removedMissionIds = unionRemovedIds(local?.removedMissionIds, remote?.removedMissionIds);
  const removedFlightLogIds = unionRemovedIds(local?.removedFlightLogIds, remote?.removedFlightLogIds);
  const stampedAt = atIso || new Date().toISOString();
  const records = gcTimedTombstones(
    ensureTombstoneRecords(
      mergeTimedTombstones(local?.boardTombstoneRecords, remote?.boardTombstoneRecords),
      [
        { kind: "inbox", ids: removedInboxIds },
        { kind: "mission", ids: removedMissionIds },
        { kind: "flightLog", ids: removedFlightLogIds },
      ],
      stampedAt,
    ),
    Date.parse(stampedAt),
  ).kept;
  return { removedInboxIds, removedMissionIds, removedFlightLogIds, records };
}

export function stampCompanyTombstones(
  company: CompanyProfile,
  extra: Partial<Omit<BoardTombstones, "records">> & { records?: TimedTombstone[] },
  atIso = new Date().toISOString(),
): CompanyProfile {
  const removedInboxIds = unionRemovedIds(company.removedInboxIds, extra.removedInboxIds);
  const removedMissionIds = unionRemovedIds(company.removedMissionIds, extra.removedMissionIds);
  const removedFlightLogIds = unionRemovedIds(company.removedFlightLogIds, extra.removedFlightLogIds);
  const records = gcTimedTombstones(
    ensureTombstoneRecords(
      mergeTimedTombstones(company.boardTombstoneRecords, extra.records),
      [
        { kind: "inbox", ids: removedInboxIds },
        { kind: "mission", ids: removedMissionIds },
        { kind: "flightLog", ids: removedFlightLogIds },
      ],
      atIso,
    ),
    Date.parse(atIso),
  ).kept;
  return hydrateCompany({
    ...company,
    removedInboxIds,
    removedMissionIds,
    removedFlightLogIds,
    boardTombstoneRecords: records,
  });
}

/** Compact expired timed records. Id lists stay, so nothing deleted comes back. */
export function gcCompanyTombstones(company: CompanyProfile, nowMs: number): CompanyProfile {
  const gc = gcTimedTombstones(hydrateTombstoneRecords(company.boardTombstoneRecords), nowMs);
  if (gc.purged.length === 0) return company;
  return hydrateCompany({ ...company, boardTombstoneRecords: gc.kept });
}

export function dropTombstonedById<T extends { id?: string | null }>(
  items: readonly T[] | null | undefined,
  removedIds: readonly string[] | null | undefined,
): T[] {
  if (!Array.isArray(items) || !items.length) return Array.isArray(items) ? [...items] : [];
  const dead = new Set(hydrateRemovedIds(removedIds));
  if (!dead.size) return [...items];
  return items.filter((row) => {
    const id = typeof row.id === "string" ? row.id.trim() : "";
    return !id || !dead.has(id);
  });
}

export function dropTombstonedLogs(
  logs: readonly FlightLogEntry[] | null | undefined,
  tombs: BoardTombstones,
): FlightLogEntry[] {
  if (!Array.isArray(logs) || !logs.length) return [];
  const deadLogs = new Set(hydrateRemovedIds(tombs.removedFlightLogIds));
  const deadTrips = new Set(hydrateRemovedIds(tombs.removedMissionIds));
  if (!deadLogs.size && !deadTrips.size) return [...logs];
  return logs.filter((row) => !deadLogs.has(row.id) && !deadTrips.has(row.tripId));
}

export type TombstoneAssignmentRow = {
  id?: string;
  requestId?: string;
  missionId?: string;
  ferryMissionId?: string;
  returnFerryMissionId?: string;
};

export function dropTombstonedAssignments<T extends TombstoneAssignmentRow>(
  assignments: readonly T[] | null | undefined,
  tombs: BoardTombstones,
): T[] {
  if (!Array.isArray(assignments) || !assignments.length) return [];
  const deadInbox = new Set(hydrateRemovedIds(tombs.removedInboxIds));
  const deadMissions = new Set(hydrateRemovedIds(tombs.removedMissionIds));
  if (!deadInbox.size && !deadMissions.size) return [...assignments];
  return assignments.filter((row) => {
    if (row.requestId && deadInbox.has(row.requestId)) return false;
    if (row.missionId && deadMissions.has(row.missionId)) return false;
    return true;
  });
}

export type PulledBoardSlice = {
  company: CompanyProfile;
  inbox: CharterRequest[];
  missions: Mission[];
  flightLogs: FlightLogEntry[];
  assignments: TombstoneAssignmentRow[];
};

/**
 * Pull merge: Nanode lists win, except tombstoned / human-complete rows.
 * Local delete ids ∪ remote delete ids stay dead.
 */
export function applyPulledBoard<TAssign extends TombstoneAssignmentRow>(
  local: PulledBoardSlice & { assignments: TAssign[] },
  remote: PulledBoardSlice & { assignments: TAssign[] },
): PulledBoardSlice & { assignments: TAssign[]; tombstones: BoardTombstones } {
  const tombstones = mergeBoardTombstones(local.company, remote.company);
  const company = stampCompanyActiveFlight(
    hydrateCompany({
      ...remote.company,
      removedCrewIds: unionRemovedIds(local.company.removedCrewIds, remote.company.removedCrewIds),
      removedInboxIds: tombstones.removedInboxIds,
      removedMissionIds: tombstones.removedMissionIds,
      removedFlightLogIds: tombstones.removedFlightLogIds,
      boardTombstoneRecords: tombstones.records,
    }),
    mergeActiveFlightBinding(local.company, remote.company),
  );
  const missions = dropTombstonedById(
    preserveAuthoritativeTickets(local.missions, remote.missions),
    [...tombstones.removedMissionIds, ...tombstoneIds(tombstones.records, "mission")],
  );
  return {
    company,
    inbox: dropTombstonedById(remote.inbox, [
      ...tombstones.removedInboxIds,
      ...tombstoneIds(tombstones.records, "inbox"),
    ]),
    missions,
    flightLogs: dropTombstonedLogs(
      preserveAuthoritativeLogs(local.flightLogs, remote.flightLogs),
      {
        ...tombstones,
        removedFlightLogIds: [...tombstones.removedFlightLogIds, ...tombstoneIds(tombstones.records, "flightLog")],
        removedMissionIds: [...tombstones.removedMissionIds, ...tombstoneIds(tombstones.records, "mission")],
      },
    ),
    assignments: dropTombstonedAssignments(remote.assignments, tombstones),
    tombstones,
  };
}
